import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Image from "next/image";
import classNames from "@lib/classNames";
import { Trans } from "@lingui/macro";
import Link from "next/link";

export enum SponsoredCarouselVariant {
  landscape = "landscape",
  square = "square",
}

export enum SponsoredClientDataElementType {
  clientCard = "client-card",
  clientCarousel = "client-carousel",
}

export interface SponsoredCardDataProps {
  title: string;
  imageUrl: string;
  link?: string;
  variant: string;
}

export type SponsoredClientDataProps =
  | {
      elementType: SponsoredClientDataElementType.clientCarousel;
      data: SponsoredDataProps;
    }
  | {
      elementType: SponsoredClientDataElementType.clientCard;
      data: SponsoredCardDataProps;
    };

export interface SponsoredDataProps {
  title: string;
  brandIcon: string;
  items: SponsoredDataItemProps[];
  variant: string;
}

export interface SponsoredDataItemProps {
  title: string;
  imageUrl: string;
  footerText?: string;
  link?: string;
}

const SponsoredCarousel: React.FC<SponsoredDataProps> = ({
  title,
  brandIcon,
  items,
  variant,
}) => {
  return (
    <div className="mt-2 flex flex-col">
      <SponsoredCarouselHeader brandIcon={brandIcon} title={title} />
      <Carousel opts={{ align: "start" }} className="w-full">
        <CarouselContent className="ml-0 gap-4">
          {items.map((item, index) => (
            <CarouselItem
              key={`${item.imageUrl}-${index}`}
              className="basis-[calc((100%-1.5rem)/2.5)] pl-0"
            >
              <div className="h-full w-full">
                {item.link ? (
                  <Link href={item.link} passHref>
                    <a
                      className="group"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SponsoredCarouselItem
                        title={item.title}
                        variant={variant}
                        imageUrl={item.imageUrl}
                        footerText={item.footerText}
                      />
                    </a>
                  </Link>
                ) : (
                  <SponsoredCarouselItem
                    title={item.title}
                    variant={variant}
                    imageUrl={item.imageUrl}
                    footerText={item.footerText}
                  />
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};

const SponsoredCarouselHeader: React.FC<{
  brandIcon: string;
  title: string;
}> = ({ brandIcon, title }) => {
  return (
    <>
      <div className="flex flex-row items-center gap-2">
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-2xl">
          <Image
            src={brandIcon}
            alt={`${brandIcon}`}
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div>
          <p className="font-sen text-xs text-text-lightest">
            <Trans>Sponsored</Trans>
          </p>
          <p className="font-sen text-sm text-text-dark line-clamp-2">
            {title}
          </p>
        </div>
      </div>
    </>
  );
};

const SponsoredCarouselItem: React.FC<{
  title: string;
  variant: string;
  imageUrl: string;
  footerText?: string;
}> = ({ title, variant, imageUrl, footerText }) => {
  return (
    <>
      <div className="flex flex-col">
        <div
          className={classNames(
            "relative mt-2 w-full overflow-hidden rounded-lg",
            variant === SponsoredCarouselVariant.landscape
              ? "aspect-[1.77]"
              : "aspect-1"
          )}
        >
          <Image
            src={imageUrl}
            alt={`${title}`}
            layout="fill"
            objectFit="cover"
          />
        </div>

        <p className="mt-2 font-poppins text-sm text-text-darkest transition-colors duration-300 group-hover:text-text-medium">
          {title}
        </p>

        {footerText && (
          <div className="mt-2 min-h-[1.25rem] font-sen text-sm text-text-lightest line-clamp-1">
            {footerText}
          </div>
        )}
      </div>
    </>
  );
};

export default SponsoredCarousel;
